import { Button } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { getLanguage, t } from "@excalidraw/excalidraw/i18n";
import clsx from "clsx";
import React, { useCallback, useRef, useState } from "react";

import { Popover } from "@excalidraw/excalidraw/components/Popover";

import { useAtomValue } from "../../../../app-jotai";

import { UploadFileIcon } from "../../../../../packages/excalidraw/components/icons";

import { userMetadataAtom } from "../../../../api_channel/channel_state_jotai";
import { useUploadFile } from "../../hooks/useUploadFile";

import "./LiveCollaborationTrigger.scss";

const UploadFileButton = ({
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const { handleUploadFile, isLoading, addFile, pdfFiles } = useUploadFile();

  const appState = useUIAppState();

  const { is_presenter } = useAtomValue(userMetadataAtom);
  const showIconOnly = appState.width < 830;

  const [showPopover, setShowPopover] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleButtonClick = useCallback(() => {
    setShowPopover(!showPopover);
  }, [showPopover]);

  const handlePopoverClose = useCallback(() => {
    setShowPopover(false);
  }, []);

  const getPopoverPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const rtl = getLanguage().rtl;
      return {
        top: rect.bottom + window.scrollY,
        left: rtl
          ? rect.left + window.scrollX - 0.1
          : rect.left + window.scrollX - window.innerWidth * 0.1,
      };
    }
    return { top: 0, left: 0 };
  };

  const { top, left } = getPopoverPosition();

  if (!is_presenter) {
    return null;
  }

  const buttonTitle = isLoading ? "Loading..." : "Upload File";

  return (
    <>
      <Button
        {...rest}
        className={clsx("collab-button", { active: true })}
        type="button"
        onSelect={handleButtonClick}
        style={{
          position: "relative",
          margin: "0 0.25rem",
          width: showIconOnly ? undefined : "auto",
        }}
        title={t("labels.fileTitle")}
        ref={buttonRef}
      >
        {showIconOnly ? UploadFileIcon : buttonTitle}
      </Button>
      {showPopover && (
        <Popover
          top={top}
          left={left}
          viewportHeight={window.innerHeight}
          viewportWidth={window.innerWidth}
          onCloseRequest={handlePopoverClose}
          fitInViewport={true}
        >
          <div
            style={{
              width: "100%",
              padding: "1rem",
              borderRadius: "0.75rem",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              backgroundColor: "var(--color-surface-mid)",
            }}
          >
            <div className="pdfFile" onClick={() => handleUploadFile()}>
              <div
                style={{
                  width: "1rem",
                  display: "inline-block",
                  marginRight: "0.5rem",
                }}
              >
                {UploadFileIcon}
              </div>
              {buttonTitle}
            </div>
            {pdfFiles.map((item) => (
              <div
                className="pdfFile"
                onClick={() => addFile(item)}
                key={item.filename}
              >
                {item.filename}
              </div>
            ))}
          </div>
        </Popover>
      )}
    </>
  );
};

export default UploadFileButton;
UploadFileButton.displayName = "UploadFileButton";
